import { styled } from '@linaria/react'
import { CircularProgress, Tooltip, Typography } from '@mui/material';
import { FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Session } from '../../../../core/domain/Session';
import formatDateWithBrowserLocale from '../../../../core/legacy/utils/date';
import ZoomOption from './Options/ZoomOption';
import ToggleSessionOption from './Options/ToggleSessionOption';
import useZoomOnSession from './use-zoom-on-session';
import { useAppSelector } from '../../../../types';
import {
    isSessionShownQuery,
    networkMapShouldRefreshQuery,
    networkExecutionStatusesQuery,
} from '../../../../core/networkMap/queries';
import Block from '../../../../design/panel/Block';
import theme from '../../../../design/theme';
import Row from '../../../../design/Row';

const SessionName = styled.p`
    color: ${theme.color.black.main};
    font-size: ${theme.font.size.small};
    font-weight: bold;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-top: 1rem;
    margin-bottom: 0;
`;

const SessionAcquisitionDate = styled.p`
    color: ${theme.color.black.main};
    font-size: ${theme.font.size.small};
    font-weight: normal;
    margin: 0.5rem 0;
`;

const SessionActions = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    gap: 16px;
    min-width: 0;
`;

const SessionDataContainer = styled.div`
    flex: 1;
    min-width: 0;
`;

export type Props = {
    session: Session;
};
const SessionSummary: FC<Props> = ({ session }) => {
    const { t } = useTranslation();
    const handleZoomOnSession = useZoomOnSession(session);
    const sessionIsShownInStore = useAppSelector(isSessionShownQuery(session.id));
    const executionStatuses = useAppSelector(networkExecutionStatusesQuery);
    const shouldRefresh = useAppSelector(networkMapShouldRefreshQuery);
    const executionStatus = executionStatuses?.find((s) => s.sessionId === session.id);
    const ongoing = executionStatus?.status === 'PLANNED' || executionStatus?.status === 'ONGOING';
    const needRefresh = shouldRefresh.find((s) => s.sessionId === session.id)?.refresh;

    return (
        <Block
            onSelect={!sessionIsShownInStore ? undefined : handleZoomOnSession}
            disabled={!sessionIsShownInStore || ongoing || needRefresh}
        >
            <Row>
                <SessionDataContainer>
                    <SessionName>{session.name}</SessionName>
                    <Tooltip title={t(`session.acquisitionDate`)} placement="top-start" arrow>
                        <SessionAcquisitionDate>
                            {formatDateWithBrowserLocale(new Date(session.metadata.acquisitionDate))}
                        </SessionAcquisitionDate>
                    </Tooltip>
                </SessionDataContainer>
                <SessionActions>
                    {ongoing && <CircularProgress />}
                    {needRefresh && (
                        <Typography textAlign="center" fontWeight="bold">
                            {t('networkMap.sessionsPanel.refreshNeeded')}
                        </Typography>
                    )}
                    {!ongoing && !needRefresh && (
                        <>
                            <ZoomOption session={session} />
                            <ToggleSessionOption session={session} />
                        </>
                    )}
                </SessionActions>
            </Row>
        </Block>
    );
};

export default memo(SessionSummary);
